// References Manager Component
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Save, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { ImageUploadField } from './AdminShared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReferencesManager = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { fetchReferences(); }, []);

  const fetchReferences = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/references/all`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setReferences(data || []);
      }
    } catch (err) {
      console.error('Error fetching references:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette référence ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/references/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchReferences();
    } catch (err) {
      console.error('Error deleting reference:', err);
    }
  };

  const handleSave = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/references/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setEditingId(null);
      fetchReferences();
    } catch (err) {
      console.error('Error saving reference:', err);
    }
  };

  const handleAdd = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/references`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setShowAddForm(false);
      setEditForm({});
      fetchReferences();
    } catch (err) {
      console.error('Error adding reference:', err);
    }
  };

  const handleReorder = async (index, direction) => {
    const newRefs = [...references];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newRefs.length) return;
    
    // Swap orders
    const currentOrder = newRefs[index].order;
    const targetOrder = newRefs[targetIndex].order;
    
    newRefs[index].order = targetOrder;
    newRefs[targetIndex].order = currentOrder;
    
    // Swap positions
    [newRefs[index], newRefs[targetIndex]] = [newRefs[targetIndex], newRefs[index]];
    setReferences(newRefs);
    
    // Update in backend
    try {
      await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/references/${newRefs[index].id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(newRefs[index])
        }),
        fetch(`${BACKEND_URL}/api/admin/references/${newRefs[targetIndex].id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(newRefs[targetIndex])
        })
      ]);
    } catch (err) { console.error('Error reordering:', err); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-slate-800">Références</h2>
        <Button onClick={() => {
          setShowAddForm(true);
          setEditForm({ image: '', title_en: '', title_fr: '', title_ar: '', order: references.length, is_active: true });
        }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {showAddForm && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-800 mb-4">Nouvelle Référence</h3>
          <div className="space-y-4">
            <ImageUploadField value={editForm.image} onChange={(url) => setEditForm({...editForm, image: url})} label="Image" />
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Titre EN</label>
                <input type="text" value={editForm.title_en || ''} onChange={(e) => setEditForm({...editForm, title_en: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Titre FR</label>
                <input type="text" value={editForm.title_fr || ''} onChange={(e) => setEditForm({...editForm, title_fr: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Titre AR</label>
                <input type="text" value={editForm.title_ar || ''} onChange={(e) => setEditForm({...editForm, title_ar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}><X className="w-4 h-4 mr-1" /> Annuler</Button>
              <Button size="sm" onClick={handleAdd}><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {references.map((ref, index) => (
          <div key={ref.id} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            {/* Edit form inline */}
            {editingId === ref.id ? (
              <div className="p-4 bg-blue-50">
                <h4 className="font-medium text-slate-800 mb-3 text-sm">Modifier la Référence</h4>
                <div className="space-y-3">
                  <ImageUploadField value={editForm.image} onChange={(url) => setEditForm({...editForm, image: url})} label="Image" />
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Titre FR</label>
                    <input type="text" value={editForm.title_fr || ''} onChange={(e) => setEditForm({...editForm, title_fr: e.target.value})} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Titre EN</label>
                    <input type="text" value={editForm.title_en || ''} onChange={(e) => setEditForm({...editForm, title_en: e.target.value})} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Titre AR</label>
                    <input type="text" value={editForm.title_ar || ''} onChange={(e) => setEditForm({...editForm, title_ar: e.target.value})} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm text-right" dir="rtl" />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}><X className="w-3 h-3 mr-1" /> Annuler</Button>
                    <Button size="sm" onClick={() => handleSave(editingId)}><Save className="w-3 h-3 mr-1" /> Enregistrer</Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <img src={ref.image} alt="" className="w-full h-32 object-cover" />
                <div className="p-3">
                  <div className="flex justify-between items-start">
                    <div className="text-sm text-slate-600 truncate flex-1">{ref.title_fr || 'Sans titre'}</div>
                    <div className="flex items-center gap-1 ml-2">
                      {/* Reorder buttons */}
                      <button
                        onClick={() => handleReorder(index, 'up')}
                        disabled={index === 0}
                        className={`p-1 rounded ${index === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                        title="Monter"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleReorder(index, 'down')}
                        disabled={index === references.length - 1}
                        className={`p-1 rounded ${index === references.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                        title="Descendre"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => { 
                          console.log('Editing reference:', ref.id, ref);
                          setEditForm({...ref}); 
                          setEditingId(ref.id); 
                        }} 
                        className="p-1 hover:bg-slate-100 rounded"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4 text-slate-400" />
                      </button>
                      <button onClick={() => handleDelete(ref.id)} className="p-1 hover:bg-slate-100 rounded" title="Supprimer"><Trash2 className="w-4 h-4 text-red-400" /></button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {references.length === 0 && !showAddForm && <div className="text-center py-12 text-slate-500">Aucune référence.</div>}
    </div>
  );
};

export default ReferencesManager;
